export type TextSize = 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';

interface TextSizeConfig {
  fontSize: string;
  fontWeight: string;
  lineHeight: string;
  letterSpacing: string;
}

export const TEXT_SIZE_MAP: Record<TextSize, TextSizeConfig> = {
  xs: {
    fontSize: 'var(--text-xs)',
    fontWeight: 'var(--font-weight-semibold)',
    lineHeight: 'var(--text-xs-line-height, var(--leading-loose))',
    letterSpacing: 'var(--text-xs-letter-spacing, var(--tracking-normal))',
  },
  sm: {
    fontSize: 'var(--text-sm)',
    fontWeight: 'var(--font-weight-semibold)',
    lineHeight: 'var(--text-sm-line-height, var(--leading-relaxed))',
    letterSpacing: 'var(--text-sm-letter-spacing, var(--tracking-tight))',
  },
  base: {
    fontSize: 'var(--text-base)',
    fontWeight: 'var(--font-weight-semibold)',
    lineHeight: 'var(--text-base-line-height, var(--leading-loose))',
    letterSpacing: 'var(--text-base-letter-spacing, var(--tracking-normal))',
  },
  lg: {
    fontSize: 'var(--text-lg)',
    fontWeight: 'var(--font-weight-bold)',
    lineHeight: 'var(--text-lg-line-height, var(--leading-relaxed))',
    letterSpacing: 'var(--text-lg-letter-spacing, var(--tracking-normal))',
  },
  xl: {
    fontSize: 'var(--text-xl)',
    fontWeight: 'var(--font-weight-semibold)',
    lineHeight: 'var(--text-xl-line-height, var(--leading-snug))',
    letterSpacing: 'var(--text-xl-letter-spacing, var(--tracking-tight))',
  },
  '2xl': {
    fontSize: 'var(--text-2xl)',
    fontWeight: 'var(--font-weight-semibold)',
    lineHeight: 'var(--text-2xl-line-height, var(--leading-snug))',
    letterSpacing: 'var(--text-2xl-letter-spacing, var(--tracking-tight))',
  },
  '3xl': {
    fontSize: 'var(--text-3xl)',
    fontWeight: 'var(--font-weight-semibold)',
    lineHeight: 'var(--text-3xl-line-height, var(--leading-tight))',
    letterSpacing: 'var(--text-3xl-letter-spacing, var(--tracking-semitight))',
  },
  '4xl': {
    fontSize: 'var(--text-4xl)',
    fontWeight: 'var(--font-weight-semibold)',
    lineHeight: 'var(--text-4xl-line-height, var(--leading-tight))',
    letterSpacing: 'var(--text-4xl-letter-spacing, var(--tracking-tighter))',
  },
};

export type FontWeight = 'light' | 'normal' | 'semibold' | 'bold';

export const FONT_WEIGHT_MAP: Record<FontWeight, string> = {
  light: 'var(--font-weight-light)',
  normal: 'var(--font-weight-normal)',
  semibold: 'var(--font-weight-semibold)',
  bold: 'var(--font-weight-bold)',
};

export type TextAlignment = 'left' | 'center' | 'right' | 'justify';

export type FontFamily = 'sans' | 'display';

export const FONT_FAMILY_MAP: Record<FontFamily, string> = {
  sans: 'var(--font-sans)',
  display: 'var(--font-display)',
};

export type Gradient = 'primary' | 'secondary' | 'tertiary';

export type TextTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'p';

export type WhiteSpace = 'normal' | 'pre-line' | 'nowrap' | 'pre-wrap';

export type TextWrap = 'wrap' | 'nowrap' | 'balance' | 'pretty' | 'stable';

export interface DynamicTextConfig {
  tag?: TextTag;
  text: string;
  size?: TextSize;
  color?: string;
  fontWeight?: FontWeight | string;
  fontFamily?: FontFamily | string;
  lineHeight?: string;
  letterSpacing?: string;
  alignment?: TextAlignment;
  gradient?: Gradient;
  whiteSpace?: WhiteSpace;
  textWrap?: TextWrap;
  cssVarPrefix?: string;
}
